import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const DailyCalorieCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [tmb, setTmb] = useState('');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [goal, setGoal] = useState('maintain');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const basalMetabolicRate = parseFloat(parseBrazilianNumber(tmb)) || 0;
    if (basalMetabolicRate <= 0) return;

    const activityFactors = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      very_active: 1.9
    };

    const dailyCalories = basalMetabolicRate * (activityFactors[activityLevel as keyof typeof activityFactors] || 1.55);

    let calorieTarget = dailyCalories;
    let targetDescription = 'Manutenção';

    if (goal === 'weight_loss') {
      calorieTarget = dailyCalories * 0.8;
      targetDescription = 'Emagrecimento';
    } else if (goal === 'muscle_gain') {
      calorieTarget = dailyCalories * 1.15;
      targetDescription = 'Hipertrofia';
    }

    let proteinRatio, carbRatio, fatRatio;
    switch (goal) {
      case 'weight_loss':
        proteinRatio = 0.30;
        carbRatio = 0.40;
        fatRatio = 0.30;
        break;
      case 'muscle_gain':
        proteinRatio = 0.25;
        carbRatio = 0.50;
        fatRatio = 0.25;
        break;
      default:
        proteinRatio = 0.20;
        carbRatio = 0.50;
        fatRatio = 0.30;
        break;
    }

    const proteinCalories = calorieTarget * proteinRatio;
    const carbCalories = calorieTarget * carbRatio;
    const fatCalories = calorieTarget * fatRatio;

    const proteinGrams = proteinCalories / 4;
    const carbGrams = carbCalories / 4;
    const fatGrams = fatCalories / 9;

    const res = {
      basalMetabolicRate,
      dailyCalories: Math.round(dailyCalories),
      calorieTarget: Math.round(calorieTarget),
      targetDescription,
      protein: {
        grams: Math.round(proteinGrams),
        percentage: Math.round(proteinRatio * 100),
      },
      carbs: {
        grams: Math.round(carbGrams),
        percentage: Math.round(carbRatio * 100),
      },
      fats: {
        grams: Math.round(fatGrams),
        percentage: Math.round(fatRatio * 100),
      }
    };

    setResultState(res);
    onResultChange([
      { label: 'TMB', value: `${basalMetabolicRate.toLocaleString('pt-BR')} kcal` },
      { label: 'Objetivo', value: res.targetDescription },
      { label: 'Gasto Diário', value: `${res.calorieTarget} kcal` },
      { label: 'Proteínas', value: `${res.protein.grams}g` },
      { label: 'Carboidratos', value: `${res.carbs.grams}g` },
      { label: 'Gorduras', value: `${res.fats.grams}g` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border">
        <h2 className="text-xl font-bold text-theme-text mb-3">Gasto Calórico Diário</h2>
        <p className="text-theme-muted mb-4">
          Calcule suas necessidades energéticas diárias com base na sua TMB e nível de atividade.
        </p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="TMB (kcal)" placeholder="Ex: 1500" value={tmb} onChange={setTmb} required isInteger type="text" />
          <FormField label="Atividade" value={activityLevel} onChange={setActivityLevel} type="select" options={[
            { value: 'sedentary', label: 'Sedentário' },
            { value: 'light', label: 'Leve' },
            { value: 'moderate', label: 'Moderado' },
            { value: 'active', label: 'Ativo' },
            { value: 'very_active', label: 'Muito Ativo' }
          ]} />
          <FormField label="Objetivo" value={goal} onChange={setGoal} type="select" options={[
            { value: 'maintain', label: 'Manter peso' },
            { value: 'weight_loss', label: 'Emagrecer' },
            { value: 'muscle_gain', label: 'Hipertrofia' }
          ]} />
        </div>
        <div className="flex justify-center mt-8">
          <button onClick={calculate} className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover transition-all flex items-center gap-2">
            <Icon name="play" className="w-5 h-5" />
            Calcular Gasto
          </button>
        </div>
      </div>

      {resultState && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ResultCard title="Gasto Diário" value={`${resultState.calorieTarget} kcal`} description={resultState.targetDescription} color="indigo" />
          <ResultCard title="Proteínas" value={`${resultState.protein.grams}g`} description={`${resultState.protein.percentage}%`} color="emerald" />
          <ResultCard title="Carboidratos" value={`${resultState.carbs.grams}g`} description={`${resultState.carbs.percentage}%`} color="blue" />
          <ResultCard title="Gorduras" value={`${resultState.fats.grams}g`} description={`${resultState.fats.percentage}%`} color="amber" />
        </div>
      )}
    </div>
  );
};

export default DailyCalorieCalculator;
